import { Filter } from '~commonInfra/utils/Filter';
import { Set } from '~commonInfra/utils/Set';
import { Messages } from '~src/codegen/Localize';
import { CheckboxState } from '~src/commonInfra/components/checkbox/CheckboxState';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { FilterFieldNames } from '~src/spacesPlatform/models/IFilterClause';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { AdminProfileUpdateActionNotification } from '~src/userManagement/services/notifications/AdminProfileUpdateActionNotification';
import { SiteUsers } from '~src/userManagement/services/resources/SiteUsers';
import { ISiteUserInfo } from '~src/userManagement/users/models/ISiteUserInfo';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';
import { SiteRoles } from '~src/userManagement/users/serverApi/types/ISiteRole';

import {
  AddUsersToAdminProfileDialogBody,
  AddUsersToAdminProfileDialogBodyProps,
  AddUsersToAdminProfileDialogState,
} from '../components/AddUsersToAdminProfileDialog';
import { IAdminProfileUser } from '../models/IAdminProfile';

const ASSIGNABLE_ROLES = [SiteRoles.Creator, SiteRoles.ExplorerCanPublish, SiteRoles.Explorer];

function getQuery(
  searchText: string,
  resultOrder: IOrderClause<string>
): ResourceQuery<ISiteUserInfo> {
  return SiteUsers.instance.all()
    .withOrder([resultOrder])
    .addFilter(Filter.stringFieldInArray(FilterFieldNames.siteRole, ASSIGNABLE_ROLES))
    .addFilter(Filter.search(searchText));
}

export async function showAddUsersToAdminProfileDialog(
  profileId: string,
  profileName: string,
  existingUsers: ReadonlyArray<IAdminProfileUser>,
  onProfileUsersUpdated?: () => void
): Promise<any> {
  // Seed checkboxes for users already assigned to the profile so they appear pre-checked.
  const initialRowCheckboxState: _.Dictionary<string> = {};
  existingUsers.forEach(u => {
    initialRowCheckboxState[u.id] = CheckboxState.checked;
  });

  const initialSelectedSet = new Set<IUserInfo>((item) => item.id);
  existingUsers.forEach(u =>
    initialSelectedSet.add({ id: u.id, displayName: u.name, username: u.username } as IUserInfo)
  );

  const initialBodyProps: AddUsersToAdminProfileDialogBodyProps = {
    profileName,
    getQuery,
    initialRowCheckboxState,
  };

  const initialDialogState: AddUsersToAdminProfileDialogState = {
    selectedUserIds: existingUsers.map(u => u.id),
    selectedUserSet: initialSelectedSet,
  };

  const modalInstance = openReactActionDialog<
    AddUsersToAdminProfileDialogBodyProps,
    AddUsersToAdminProfileDialogState,
    any
  >({
    title: Messages.adminProfileUsersDialogTitle({ PROFILE_NAME: profileName }),
    bodyComponent: AddUsersToAdminProfileDialogBody,
    initialBodyProps,
    initialDialogState,
    actionName: (_bodyProps, state) =>
      Messages.confirmAddUsersToAdminProfile({ COUNT: String(state.selectedUserIds.length) }),
    actionDisabled: (_bodyProps, state) => {
      const initialIdSet = new globalThis.Set(existingUsers.map(u => u.id));
      const currentIdSet = new globalThis.Set(state.selectedUserIds);
      if (initialIdSet.size !== currentIdSet.size) return false;
      return Array.from(initialIdSet).every(id => currentIdSet.has(id));
    },
    action: async (_bodyProps, state) => {
      // The backend API (setUsersAdminProfile) is per-user and additive: calling it with a list
      // of user LUIDs assigns the admin profile to those users but does NOT remove it from anyone
      // else. There is no single "replace all users for this profile" endpoint today.
      //
      // To honour the dialog's intent (the checked state IS the desired state), we compute a diff
      // against the users who were already assigned when the dialog opened (existingUsers), then
      // issue up to two targeted API calls:
      //   1. Remove — for users who were pre-checked but the operator has now unchecked.
      //   2. Add    — for users who were unchecked but the operator has now checked.
      //
      // We intentionally do remove-before-add so that if the remove fails we haven't yet granted
      // any new access, keeping the failure mode conservative.
      //
      // The two calls are not atomic. If the remove succeeds but the add fails (or vice versa) the
      // dialog stays open (dismissDialogOnActionFailed: false) so the operator can see the error
      // and retry. A future improvement would be a profile-centric backend endpoint
      // (e.g. replaceAdminProfileUsers) that wraps both operations in a single transaction.

      const selectedLuids = state.selectedUserSet.toArray()
        .map(user => (user as ISiteUserInfo).luid || user.id);
      const selectedLuidSet = new globalThis.Set(selectedLuids);

      const existingLuids = existingUsers.map(u => u.luid);
      const existingLuidSet = new globalThis.Set(existingLuids);

      const luidsToAdd = selectedLuids.filter(luid => !existingLuidSet.has(luid));
      const luidsToRemove = existingLuids.filter(luid => !selectedLuidSet.has(luid));

      let result;

      if (luidsToRemove.length > 0) {
        result = await SiteUsers.instance.setUsersAdminProfile(luidsToRemove);
      }

      if (luidsToAdd.length > 0) {
        const addedUserSet = new Set<IUserInfo>(item => item.id);
        state.selectedUserSet.toArray()
          .filter(u => luidsToAdd.includes((u as ISiteUserInfo).luid || u.id))
          .forEach(u => addedUserSet.add(u));
        result = await SiteUsers.instance.setUsersAdminProfile(luidsToAdd, profileId);
        AdminProfileUpdateActionNotification.instance.notify(result, addedUserSet);
      }

      onProfileUsersUpdated?.();
      return result;
    },
    dialogWidth: TabDialogWidth.width700px,
    dismissDialogOnActionFailed: false,
    showCloseButton: true,
  });

  return modalInstance.result;
}

export const AddUsersToAdminProfileDialogAction = {
  showAddUsersToAdminProfileDialog,
};
